import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";

import AccountMenu from "@/components/AccountMenu";
import { Wordmark } from "@/components/PublicShell";
import ThemeToggle from "@/components/ThemeToggle";

export interface NavLinkItem {
  to: string;
  label: string;
  end?: boolean;
  /** A file to save rather than a screen to open: rendered as a download link. */
  download?: boolean;
}

/** A labelled dropdown of links, grouped under small headings. */
export interface NavMenu {
  label: string;
  sections: Array<{ title: string; items: NavLinkItem[] }>;
}

export type NavEntry = NavLinkItem | NavMenu;

export function isMenu(entry: NavEntry): entry is NavMenu {
  return "sections" in entry;
}

//: The landing page, reachable signed in. "/" is the fast path to your pool
//: once you are in, so the page that explains the app lives at /home.
const HOME: NavLinkItem = { to: "/home", label: "Home" };

/**
 * The one top bar every signed-in screen uses.
 *
 * Wordmark, the screen links when there is a pool to navigate, then on the
 * right whatever is about the whole visit: the pool switcher, the theme, the
 * account. A link entry is a link; a menu entry is a dropdown of grouped
 * links on a desktop and a headed group inside the phone's menu, where a
 * dropdown inside a dropdown would be two taps too many. Below the `md`
 * breakpoint everything folds behind the button on the left.
 */
export default function TopBar({
  nav,
  switcher,
  wide = false,
}: {
  nav?: NavEntry[];
  switcher?: ReactNode;
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const links: NavEntry[] = [HOME, ...(nav ?? [])];

  // Navigating closes the menu; so does Escape.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header
      className="no-print sticky top-0 z-20 border-b border-(--color-border-subtle) bg-(--color-surface)/90 backdrop-blur"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Wider than the content column on purpose: a bar that holds the
          wordmark, six links, a pool name and three controls needs the room,
          and a bar is read as the page's edge rather than its text. */}
      <div className={`mx-auto flex items-center gap-2 px-3 py-2 sm:px-4 ${wide ? "max-w-6xl" : "max-w-5xl"}`}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Menu"}
          aria-expanded={open}
          aria-controls="site-menu"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-(--color-ink-muted) hover:bg-(--color-surface-sunken) hover:text-(--color-ink) md:hidden"
        >
          {open ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>

        <Wordmark compact={Boolean(nav || switcher)} />

        <nav aria-label="Screens" className="ml-2 hidden items-center gap-0.5 md:flex">
          {links.map((entry) =>
            isMenu(entry) ? (
              <DesktopMenu key={entry.label} menu={entry} />
            ) : (
              <NavLink key={entry.to} to={entry.to} end={entry.end} className={desktopLinkClass}>
                {entry.label}
              </NavLink>
            ),
          )}
        </nav>

        <div className="ml-auto flex min-w-0 shrink items-center gap-1">
          {switcher}
          <ThemeToggle />
          <AccountMenu />
        </div>
      </div>

      {open && (
        <nav id="site-menu" aria-label="Screens" className="border-t border-(--color-border-subtle) md:hidden">
          <ul className="mx-auto max-w-3xl py-1">
            {links.map((entry) =>
              isMenu(entry) ? (
                entry.sections.map((section) => (
                  <li key={`${entry.label}-${section.title}`}>
                    <p className="unit px-4 pt-3 pb-1">{section.title}</p>
                    <ul>
                      {section.items.map((item) => (
                        <li key={item.to}>
                          <PanelLink item={item} />
                        </li>
                      ))}
                    </ul>
                  </li>
                ))
              ) : (
                <li key={entry.to}>
                  <PanelLink item={entry} />
                </li>
              ),
            )}
          </ul>
        </nav>
      )}
    </header>
  );
}

function desktopLinkClass({ isActive }: { isActive: boolean }): string {
  return `rounded-md px-3 py-1.5 text-sm font-medium ${
    isActive
      ? "bg-(--color-surface-sunken) text-(--color-ink)"
      : "text-(--color-ink-muted) hover:bg-(--color-surface-sunken) hover:text-(--color-ink)"
  }`;
}

function panelLinkClass({ isActive }: { isActive: boolean }): string {
  return `block border-l-4 px-4 py-3 text-base ${
    isActive
      ? "border-l-(--color-brand) font-semibold text-(--color-ink)"
      : "border-l-transparent text-(--color-ink-muted)"
  }`;
}

function PanelLink({ item }: { item: NavLinkItem }) {
  if (item.download) {
    return (
      <a href={item.to} download className={panelLinkClass({ isActive: false })}>
        {item.label}
      </a>
    );
  }
  return (
    <NavLink to={item.to} end={item.end} className={panelLinkClass}>
      {item.label}
    </NavLink>
  );
}

/** "More ▾": a button that is lit while any of its screens is open. */
function DesktopMenu({ menu }: { menu: NavMenu }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    function onPointer(event: PointerEvent) {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = menu.sections.some((s) =>
    s.items.some((i) => !i.download && (pathname === i.to || pathname.startsWith(`${i.to}/`))),
  );

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={`${desktopLinkClass({ isActive: active })} flex items-center gap-1`}
      >
        {menu.label}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden className="text-(--color-ink-faint)">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div role="menu" aria-label={menu.label} className="card absolute left-0 z-30 mt-1 w-64 overflow-hidden py-1 shadow-lg">
          {menu.sections.map((section, index) => (
            <div key={section.title} className={index > 0 ? "mt-1 border-t border-(--color-border-subtle) pt-1" : ""}>
              <p className="unit px-4 pt-2 pb-1">{section.title}</p>
              {section.items.map((item) =>
                item.download ? (
                  <a
                    key={item.to}
                    href={item.to}
                    download
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm hover:bg-(--color-surface-sunken)"
                  >
                    {item.label}
                  </a>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm hover:bg-(--color-surface-sunken) ${isActive ? "font-semibold" : ""}`
                    }
                  >
                    {item.label}
                  </NavLink>
                ),
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
